import { confirmPasswordReset, getAuth } from 'firebase/auth';
import React from 'react';
import { InputAdornment, IconButton, CircularProgress, Stack } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Root,
  StyledHeading,
  StyledTextField,
  StyledTypography,
} from '../components/styles/styles';
import StyledSection from '../components/styles/StyledSection';
import { StyledButton, StyledButtonGroup } from '../components/styles/StyledButtons';

interface ResetPasswordProps {
  code: string | null;
  navigate?: (path: string) => void;
}

interface State {
  password: string;
  confirmPassword: string;
  passwordVisible: boolean;
  error: string | null;
  submitting?: boolean;
}

export default class ResetPassword extends React.Component<ResetPasswordProps, State> {
  constructor(props: ResetPasswordProps) {
    super(props);
    this.state = {
      password: '',
      confirmPassword: '',
      passwordVisible: false,
      error: null,
    };
  }

  handleResetPassword = async () => {
    this.setState({ submitting: true });
    const auth = getAuth();

    try {
      await confirmPasswordReset(auth, this.props.code!, this.state.password);
      if (this.props.navigate) {
        this.props.navigate('/');
      }
    } catch (error: any) {
      this.setState({
        error: error?.message || 'Something went wrong',
        submitting: false,
      });
    }
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    this.setState({ ...this.state, [name]: value });
  };

  togglePasswordVisibility = () => {
    this.setState((prevState) => ({
      passwordVisible: !prevState.passwordVisible,
    }));
  };

  handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const { password, confirmPassword } = this.state;
    if (password !== confirmPassword) {
      this.setState({ error: 'Passwords do not match' });
      return;
    }
    this.handleResetPassword();
  };

  render() {
    const { password, confirmPassword, passwordVisible, submitting, error } = this.state;

    return (
      <Root>
        <StyledSection tone="tone1">
          <StyledHeading>Reset Password</StyledHeading>
          <StyledHeading headerVariant="subheading">
            Enter your new password below to reset your account.
          </StyledHeading>
          <form onSubmit={this.handleSubmit}>
            <Stack spacing={2}>
              <StyledTextField
                label="Password"
                name="password"
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={this.handleChange}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={this.togglePasswordVisibility}>
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                label="Confirm Password"
                name="confirmPassword"
                type={passwordVisible ? 'text' : 'password'}
                value={confirmPassword}
                onChange={this.handleChange}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={this.togglePasswordVisibility}>
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {error && <StyledTypography color="error">{error}</StyledTypography>}
              <StyledButtonGroup>
                <StyledButton type="submit" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={24} style={{ marginRight: '0.5rem' }} />
                  ) : (
                    'Reset Password'
                  )}
                </StyledButton>
              </StyledButtonGroup>
            </Stack>
          </form>
        </StyledSection>
      </Root>
    );
  }
}
